import { action, computed, observable } from 'mobx';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { TerraceDesignToolsController, TerraceChannelTemplateController, TerraceReachController, } from 'cd-eft/services/eftSetApi';
import { ShapeControlDTO, TerraceDesignControlDTO, TerraceSlopeAndLimits, UpdateTerraceDesignControlDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import stores from 'cd-eft/models/Stores';
import { ITerraceVertexParamsDTO, IReachTemplateForm, reachTemplateFormDefault, SlopeModeChannelEnum, SlopeModeToLimitEnum, TerraceUiState } from 'cd-eft/models/Stores/TerraceDesignStore';
import { formatRound } from 'cd-eft/utils/mathUtilities';

interface IOrientationFormData {
  flagLineLocation: string;
  offsetFromAlignment: number;
}

interface IShapeControls {
  overfillMode: string;
  backCutEnabled: boolean;
  overfillAmount: number;
  freeboardMode: string;
  minimumSlopeWidth: number;
  freeboard: number;
  backCutSlopeGrade: number;
  startMode: string;
  endMode: string;
  startValue: number;
  endValue: number;
  startLimit: number;
  endLimit: number;
  gradeChannelStart: boolean;
  gradeChannelEnd: boolean;
  overfillAmountDisabled: boolean;
  freeboardDisabled: boolean;
}

export interface IDefaultTerraceTemplateReachRow {
  cutSlope: number;
  cutSlopeMode: number;
  frontSlope: number;
  frontSlopeMode: number;
  backSlope: number;
  backSlopeMode: number;
  bottomWidth: number;
  frontHeight: number;
  topWidth: number;
  terraceChannelTemplateID: number;
  templateName: string;
  comments: string;
  arrayOrder: number;
  slopeLimitJson: string;
}

const { terraceDesignStore, designStore } = stores.AppStore;

const TemplateDefaults = {
  orientationFormData: {
    flagLineLocation: 'UPSTREAM_TOE',
    offsetFromAlignment: 0,
  },
  shapeControls: {
    overfillMode: 'PERCENT',
    backCutEnabled: false,
    overfillAmount: 10,
    freeboardMode: 'NONE',
    minimumSlopeWidth: 5,
    freeboard: 0,
    backCutSlopeGrade: 1,
    startMode: 'RATIO_BASED',
    endMode: 'WIDTH_BASED',
    startValue: 15,
    endValue: 6,
    startLimit: 50,
    endLimit: 20,
    gradeChannelStart: true,
    gradeChannelEnd: true,
  },
}

export class TerraceTemplateVM {
  history: any;
  constructor(history) {
    this.history = history;
  }

  // free: whenever any input on Terrace Input Page changes, the state is set free
  // otherState: TerraceInputSave, TerraceInputProceed, ChannelDesignSave ...
  @computed get uiState(): 'free' | 'TerraceTemplateSave' | 'TerraceTemplateProceed' | 'otherState' {
    let uiState: 'free' | 'TerraceTemplateSave' | 'TerraceTemplateProceed' | 'otherState'
    const globalUIState = terraceDesignStore.uiState;

    // removed globalUIState === undefined from first IF (new terrace case),
    // so, it goes with uiState = 'otherState' and disable both saving buttons
    if (globalUIState === 'TerraceInputProceed' || (terraceDesignStore.terraceInputsChanged && terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId !== 0)) {
      uiState = 'free'
    } else if (globalUIState === 'TerraceTemplateSave' || globalUIState === 'TerraceTemplateProceed') {
      uiState = globalUIState
    } else {
      uiState = 'otherState'
    }
    return uiState
  }

  @observable orientationFormData: IOrientationFormData = {
    flagLineLocation: 'UPSTREAM_TOE',
    offsetFromAlignment: 0,
  };
  @observable flagLineLocationOptions = [{ name: 'Channel Center', value: 'CHANNEL_CENTER' }, { name: 'Ridge Center', value: 'RIDGE_CENTER' }, { name: 'Uphill Top Corner', value: 'UPHILL_TOP_CORNER' }, { name: 'Upstream Toe', value: 'UPSTREAM_TOE' }];
  @observable crossSectionSlopeBasisOption = [
    { name: 'Ratio', value: 'Ratio', optionDisabled: true },
    { name: 'Width', value: 'Width' },
  ];
  @observable shapeControls: IShapeControls = {
    overfillMode: 'PERCENT',
    backCutEnabled: false,
    overfillAmount: 10,
    overfillAmountDisabled: false,
    freeboardMode: 'NONE',
    minimumSlopeWidth: 5,
    freeboard: 0,
    freeboardDisabled: true,
    backCutSlopeGrade: 5,
    startMode: 'RATIO_BASED',
    endMode: 'WIDTH_BASED',
    startValue: 5,
    endValue: 5,
    startLimit: 10,
    endLimit: 10,
    gradeChannelStart: true,
    gradeChannelEnd: true,
  };
  @observable shapeOverallModeOptions = [{ name: 'None', value: 'NONE' }, { name: 'Height', value: 'HEIGHT' }, { name: 'Percent', value: 'PERCENT' }];
  @observable shapeFreeboardModeOptions = [{ name: 'None', value: 'NONE' }, { name: 'Full Length', value: 'FULL_LENGTH' }, { name: 'Storage', value: 'STORAGE' }];
  @observable shapeBackStartModeOptions = [{ name: 'Ratio Based', value: 'RATIO_BASED' }, { name: 'Width Based', value: 'WIDTH_BASED' }];
  @observable shapeBackEndModeOptions = [{ name: 'Ratio Based', value: 'RATIO_BASED' }, { name: 'Width Based', value: 'WIDTH_BASED' }];
  @observable isLoading: boolean = false;
  @observable onClickInformation: boolean = false;
  @observable orientationRadioControl: number = 1;
  @observable defaultTemplateRadioControl: number = 1;
  @observable reachTemplateForm: IReachTemplateForm = { ...reachTemplateFormDefault };
  @observable terraceTemplateReachesObject: IDefaultTerraceTemplateReachRow[] = [];
  @observable displayTemplateTable: boolean = false;
  @observable templateName: string | undefined = 'BroadBase'
  @observable isUserDefinedTemplate: number = 0;
  @observable shapeControlsHasWarnings: boolean = false;

  @computed get disableSaveButton() {
    const formValidationCopy = JSON.parse(JSON.stringify(this.formValidation));

    if (!this.reachTemplateForm.slopeLimits[0].isLimitEnforced) {
      formValidationCopy.reachTemplateForm.cutSlopeLimit = false;
    }
    if (!this.reachTemplateForm.slopeLimits[1].isLimitEnforced) {
      formValidationCopy.reachTemplateForm.frontSlopeLimit = false;
    }
    if (!this.reachTemplateForm.slopeLimits[2].isLimitEnforced) {
      formValidationCopy.reachTemplateForm.backSlopeLimit = false;
    }
    if (!this.shapeControls.gradeChannelStart) {
      formValidationCopy.shapeControls.startValue = false;
      formValidationCopy.shapeControls.startLimit = false;
    }
    if (!this.shapeControls.gradeChannelEnd) {
      formValidationCopy.shapeControls.endValue = false;
      formValidationCopy.shapeControls.endLimit = false;
    }
    if (!this.shapeControls.backCutEnabled) {
      formValidationCopy.shapeControls.minimumSlopeWidth = false;
      formValidationCopy.shapeControls.backCutSlopeGrade = false;
    }

    const { orientationFormData, reachTemplateForm, shapeControls } = formValidationCopy;
    const object = { orientationFormData, reachTemplateForm, shapeControls };
    return !this.isAllFalse(object) || this.isLoading || this.uiState === 'TerraceTemplateSave' || this.uiState === 'TerraceTemplateProceed' || this.uiState === 'otherState';
  }

  @computed get disableSaveAndProceedButton() {
    const formValidationCopy = JSON.parse(JSON.stringify(this.formValidation));

    if (!this.reachTemplateForm.slopeLimits[0].isLimitEnforced) {
      formValidationCopy.reachTemplateForm.cutSlopeLimit = false;
    }
    if (!this.reachTemplateForm.slopeLimits[1].isLimitEnforced) {
      formValidationCopy.reachTemplateForm.frontSlopeLimit = false;
    }
    if (!this.reachTemplateForm.slopeLimits[2].isLimitEnforced) {
      formValidationCopy.reachTemplateForm.backSlopeLimit = false;
    }
    if (!this.shapeControls.gradeChannelStart) {
      formValidationCopy.shapeControls.startValue = false;
      formValidationCopy.shapeControls.startLimit = false;
    }
    if (!this.shapeControls.gradeChannelEnd) {
      formValidationCopy.shapeControls.endValue = false;
      formValidationCopy.shapeControls.endLimit = false;
    }
    if (!this.shapeControls.backCutEnabled) {
      formValidationCopy.shapeControls.minimumSlopeWidth = false;
      formValidationCopy.shapeControls.backCutSlopeGrade = false;
    }

    const { orientationFormData, reachTemplateForm, shapeControls } = formValidationCopy;
    const object = { orientationFormData, reachTemplateForm, shapeControls };

    return !this.isAllFalse(object) || this.isLoading || this.uiState === 'TerraceTemplateProceed' || this.uiState === 'otherState';
  }

  @observable formValidation = {
    orientationFormData: {
      offsetFromAlignment: false,
    },
    reachTemplateForm:
    {
      bottomWidth: false,
      frontHeight: false,
      topWidth: false,

      cutSlopeValue: false,
      cutSlopeLimit: false,

      frontSlopeValue: false,
      frontSlopeLimit: false,

      backSlopeValue: false,
      backSlopeLimit: false

    },
    shapeControls: {
      overfillAmount: false,
      minimumSlopeWidth: false,
      freeboard: false,
      backCutSlopeGrade: false,
      startValue: false,
      endValue: false,
      startLimit: false,
      endLimit: false,
    },
    formErrors: {
      // Orientation section 
      offsetFromAlignment: '',

      // Cross Section Controls section 
      cutSlopeLimit: '',
      cutSlopeValue: '',
      frontSlopeLimit: '',
      frontSlopeValue: '',
      backSlopeLimit: '',
      backSlopeValue: '',
      bottomWidth: '',
      frontHeight: '',
      topWidth: '',

      // Shape Controls section 
      overfillAmount: '',
      freeboard: '',
      minimumSlopeWidth: '',
      backCutSlopeGrade: '',
      startValue: '',
      endValue: '',
      startLimit: '',
      endLimit: '',
    },
  };

  resetCrossSectionControlValidation() {
    const crossSectionControlObj = this.formValidation.reachTemplateForm;
    for (const key in crossSectionControlObj) {
      crossSectionControlObj[key] = false;
    }
    const formErrors = this.formValidation.formErrors;
    formErrors.cutSlopeLimit = '';
    formErrors.cutSlopeValue = '';
    formErrors.frontSlopeLimit = '';
    formErrors.frontSlopeValue = '';
    formErrors.backSlopeLimit = '';
    formErrors.backSlopeValue = '';
    formErrors.bottomWidth = '';
    formErrors.frontHeight = '';
    formErrors.topWidth = '';
  }



  @action.bound onClickInformationToggle = () => {
    this.onClickInformation = !this.onClickInformation;
  };

  get leftRadioControl(): boolean {
    return this.orientationRadioControl === 0;
  }
  get rightRadioControl(): boolean {
    return this.orientationRadioControl === 1;
  }

  @action.bound async handleInputChange(event, index?) {
    const { name, value, type } = event.target;
    // this.disableSaveButton = false;
    // this.disableSaveAndProceedButton = false;

    if (type === 'number' && value.length > 7) {
      return;
    }

    const val = value === '' ? '' : parseFloat(value);
    switch (name) {
      case 'uphillDirectionLeft':
        this.orientationRadioControl = 0;
        break;
      case 'uphillDirectionRight':
        this.orientationRadioControl = 1;
        break;
      case 'flag-line-location':
        this.orientationFormData.flagLineLocation = value;
        break;
      case 'offset-from-alignment':
        this.orientationFormData.offsetFromAlignment = value;
        break;
      case `bottomWidth`:
        this.reachTemplateForm.bottomWidth = val;
        break;
      case `frontHeight`:
        this.reachTemplateForm.frontHeight = val;
        break;
      case `topWidth`:
        this.reachTemplateForm.topWidth = val;
        break;
      case `enableCutSlope`:
        this.reachTemplateForm.slopeLimits[0].isLimitEnforced = !!this.reachTemplateForm.slopeLimits[0].isLimitEnforced ? 0 : 1;
        break;
      case `enableFrontSlope`:
        this.reachTemplateForm.slopeLimits[1].isLimitEnforced = !!this.reachTemplateForm.slopeLimits[1].isLimitEnforced ? 0 : 1;
        break;
      case `enableBackSlope`:
        this.reachTemplateForm.slopeLimits[2].isLimitEnforced = !!this.reachTemplateForm.slopeLimits[2].isLimitEnforced ? 0 : 1;
        break;
      case `cutSlopeMode`:
        this.reachTemplateForm.slopeLimits[0].slopeMode = value;
        this.reachTemplateForm.slopeLimits[0].limitingMode = SlopeModeToLimitEnum[value]
        break;
      case `frontSlopeMode`:
        this.reachTemplateForm.slopeLimits[1].slopeMode = value;
        this.reachTemplateForm.slopeLimits[1].limitingMode = SlopeModeToLimitEnum[value]
        break;
      case `backSlopeMode`:
        this.reachTemplateForm.slopeLimits[2].slopeMode = value;
        this.reachTemplateForm.slopeLimits[2].limitingMode = SlopeModeToLimitEnum[value]
        break;
      case `cutSlopeLimit`:
        this.reachTemplateForm.slopeLimits[0].slopeLimit = value;
        break;
      case `cutSlopeValue`:
        this.reachTemplateForm.slopeLimits[0].slopeValue = val;
        break;
      case `frontSlopeLimit`:
        this.reachTemplateForm.slopeLimits[1].slopeLimit = value;
        break;
      case `frontSlopeValue`:
        this.reachTemplateForm.slopeLimits[1].slopeValue = val;
        break;
      case `backSlopeLimit`:
        this.reachTemplateForm.slopeLimits[2].slopeLimit = value;
        break;
      case `backSlopeValue`:
        this.reachTemplateForm.slopeLimits[2].slopeValue = val;
        break;
      case `overfill-mode`:
        this.shapeControls.overfillMode = value;
        if (value === 'NONE') {
          this.shapeControls.overfillAmountDisabled = true;
          this.shapeControls.overfillAmount = 0;
          this.formValidation.shapeControls.overfillAmount = false;
          this.formValidation.formErrors.overfillAmount = '';
        } else {
          this.shapeControls.overfillAmountDisabled = false;
        }
        break;
      case `backCutEnabled`:
        this.shapeControls.backCutEnabled = !this.shapeControls.backCutEnabled;
        break;
      case `overfill-amount`:
        this.shapeControls.overfillAmount = value;
        break;
      case `freeboard-mode`:
        this.shapeControls.freeboardMode = value;
        if (value === 'NONE') {
          this.shapeControls.freeboardDisabled = true;
          this.shapeControls.freeboard = 0;
          this.formValidation.shapeControls.freeboard = false;
          this.formValidation.formErrors.freeboard = ''
        } else {
          this.shapeControls.freeboardDisabled = false;
        }
        break;
      case `minimum-slope-width`:
        this.shapeControls.minimumSlopeWidth = value;
        break;
      case `freeboard`:
        this.shapeControls.freeboard = value;
        break;
      case `back-cut-slope-grade`:
        this.shapeControls.backCutSlopeGrade = value;
        break;
      case `start-mode`:
        this.shapeControls.startMode = value;
        break;
      case `end-mode`:
        this.shapeControls.endMode = value;
        break;
      case `start-value`:
        this.shapeControls.startValue = value;
        break;
      case `end-value`:
        this.shapeControls.endValue = value;
        break;
      case `start-limit`:
        this.shapeControls.startLimit = value;
        break;
      case `end-limit`:
        this.shapeControls.endLimit = value;
        break;
      case `grade-channel-start`:
        this.shapeControls.gradeChannelStart = !this.shapeControls.gradeChannelStart;
        break;
      case `grade-channel-end`:
        this.shapeControls.gradeChannelEnd = !this.shapeControls.gradeChannelEnd;
        break;
    }

    if ((name === 'bottomWidth' ||
      name === `frontHeight` ||
      name === `topWidth` ||
      name === `enableCutSlope` ||
      name === `enableFrontSlope` ||
      name === `enableBackSlope` ||
      name === `cutSlopeMode` ||
      name === `frontSlopeMode` ||
      name === `backSlopeMode` ||
      name === `cutSlopeLimit` ||
      name === `frontSlopeLimit` ||
      name === `backSlopeLimit` ||
      name === `cutSlopeValue` ||
      name === `frontSlopeValue` ||
      name === `backSlopeValue`)
    ) {
      const templatesAreEqual = this.templatesAreEqual(this.reachTemplateForm, terraceDesignStore.initialTerraceTemplate);
      if (templatesAreEqual) {
        this.resetToInitialTemplate();
      } else {
        if (terraceDesignStore.terraceInputsChanged) terraceDesignStore.terraceInputsChanged = false;
        this.isUserDefinedTemplate = 1;
        this.reachTemplateForm.templateName = 'User Defined';
        this.templateName = 'User Defined';
        this.defaultTemplateRadioControl = 0;
        terraceDesignStore.terraceInputsChanged = true;
      }
    } else {
      terraceDesignStore.terraceInputsChanged = true;
    }
  }

  @action.bound async handleOnBlur(e) {
    const { name, value, type } = e.target;
    terraceDesignStore.terraceInputsChanged = true;

    const numericValue = formatRound(parseFloat(value), 2);

    if (type === 'number' && !isNaN(numericValue)) {
      const element = { target: { name: name, value: numericValue, type: 'number' } };
      this.handleInputChange(element);
      await this.validateInput(name, numericValue);

    } else {
      await this.validateInput(name, value);
    }

  }

  @action.bound async validateInput(name, value) {
    switch (name) {
      // Orientation section 
      case 'offset-from-alignment': {
        if (value === '') {
          this.formValidation.orientationFormData.offsetFromAlignment = true;
          this.formValidation.formErrors.offsetFromAlignment = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.orientationFormData.offsetFromAlignment = false;
          this.formValidation.formErrors.offsetFromAlignment = '';
        }
        break;
      }

      // Cross Section Controls section
      case 'bottomWidth': {
        if (value === '' || value < 0) {
          this.formValidation.reachTemplateForm.bottomWidth = true;
          this.formValidation.formErrors.bottomWidth = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_14', undefined, 'Bottom Width');
        } else {
          this.formValidation.reachTemplateForm.bottomWidth = false;
          this.formValidation.formErrors.bottomWidth = '';
        }
        break;
      }

      case 'frontHeight': {
        if (value === '' || value < 0) {
          this.formValidation.reachTemplateForm.frontHeight = true;
          this.formValidation.formErrors.frontHeight = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_14', undefined, 'Front Height');
        } else {
          this.formValidation.reachTemplateForm.frontHeight = false;
          this.formValidation.formErrors.frontHeight = '';
        }
        break;
      }

      case 'topWidth': {
        if (value === '' || value < 0) {
          this.formValidation.reachTemplateForm.topWidth = true;
          this.formValidation.formErrors.topWidth = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_14', undefined, 'Top Width');
        } else {
          this.formValidation.reachTemplateForm.topWidth = false;
          this.formValidation.formErrors.topWidth = '';
        }
        break;
      }

      case 'cutSlopeValue': {
        if (value === '' || value <= 0) {
          this.formValidation.reachTemplateForm.cutSlopeValue = true;
          this.formValidation.formErrors.cutSlopeValue = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Cut Slope Value');
        } else {
          this.formValidation.reachTemplateForm.cutSlopeValue = false;
          this.formValidation.formErrors.cutSlopeValue = '';
        }
        break;
      }

      case 'cutSlopeLimit': {
        if (value === '' || value <= 0) {
          this.formValidation.reachTemplateForm.cutSlopeLimit = true;
          this.formValidation.formErrors.cutSlopeLimit = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Cut Slope Limit');
        } else {
          this.formValidation.reachTemplateForm.cutSlopeLimit = false;
          this.formValidation.formErrors.cutSlopeLimit = '';
        }
        break;
      }

      case 'frontSlopeValue': {
        if (value === '' || value <= 0) {
          this.formValidation.reachTemplateForm.frontSlopeValue = true;
          this.formValidation.formErrors.frontSlopeValue = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Front Slope Value');
        } else {
          this.formValidation.reachTemplateForm.frontSlopeValue = false;
          this.formValidation.formErrors.frontSlopeValue = '';
        }
        break;
      }

      case 'backSlopeValue': {
        if (value === '' || value <= 0) {
          this.formValidation.reachTemplateForm.backSlopeValue = true;
          this.formValidation.formErrors.backSlopeValue = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Back Slope Value');
        } else {
          this.formValidation.reachTemplateForm.backSlopeValue = false;
          this.formValidation.formErrors.backSlopeValue = '';
        }
        break;
      }

      case 'backSlopeLimit': {
        if (value === '' || value <= 0) {
          this.formValidation.reachTemplateForm.backSlopeLimit = true;
          this.formValidation.formErrors.backSlopeLimit = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Back Slope Limit');
        } else {
          this.formValidation.reachTemplateForm.backSlopeLimit = false;
          this.formValidation.formErrors.backSlopeLimit = '';
        }
        break;
      }

      case 'frontSlopeLimit': {
        if (value === '' || value <= 0) {
          this.formValidation.reachTemplateForm.frontSlopeLimit = true;
          this.formValidation.formErrors.frontSlopeLimit = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Front Slope Limit');
        } else {
          this.formValidation.reachTemplateForm.frontSlopeLimit = false;
          this.formValidation.formErrors.frontSlopeLimit = '';
        }
        break;
      }

      // Shape Controls section 
      case 'overfill-amount': {
        if (value === '' || value < 0) {
          this.formValidation.shapeControls.overfillAmount = true;
          this.formValidation.formErrors.overfillAmount = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_14', undefined, 'Overall Amount');
        } else {
          this.formValidation.shapeControls.overfillAmount = false;
          this.formValidation.formErrors.overfillAmount = '';
        }
        break;
      }

      case 'minimum-slope-width': {
        if (this.shapeControls.backCutEnabled) {
          if (value === '' || value <= 0) {
            this.formValidation.shapeControls.minimumSlopeWidth = true;
            this.formValidation.formErrors.minimumSlopeWidth = 'usa-input--error';
            await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Minimum Slope Width');
          } else {
            this.formValidation.shapeControls.minimumSlopeWidth = false;
            this.formValidation.formErrors.minimumSlopeWidth = '';
          }
        }
        break;
      }

      case 'freeboard': {
        if (value === '' || value < 0) {
          this.formValidation.shapeControls.freeboard = true;
          this.formValidation.formErrors.freeboard = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_14', undefined, 'Freeboard');
        } else {
          this.formValidation.shapeControls.freeboard = false;
          this.formValidation.formErrors.freeboard = '';
        }
        break;
      }

      case 'back-cut-slope-grade': {
        if (this.shapeControls.backCutEnabled) {
          if (value === '' || value <= 0) {
            this.formValidation.shapeControls.backCutSlopeGrade = true;
            this.formValidation.formErrors.backCutSlopeGrade = 'usa-input--error';
            await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Back Cut-Slope Grade');
          } else {
            this.formValidation.shapeControls.backCutSlopeGrade = false;
            this.formValidation.formErrors.backCutSlopeGrade = '';
          }
        }
        break;
      }

      case 'start-value': {
        if (value === '' || value <= 0) {
          this.formValidation.shapeControls.startValue = true;
          this.formValidation.formErrors.startValue = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Embankment Start Slope Value');
        } else {
          this.formValidation.shapeControls.startValue = false;
          this.formValidation.formErrors.startValue = '';
        }
        break;
      }

      case 'end-value': {
        if (value === '' || value <= 0) {
          this.formValidation.shapeControls.endValue = true;
          this.formValidation.formErrors.endValue = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Embankment End Slope Value');
        } else {
          this.formValidation.shapeControls.endValue = false;
          this.formValidation.formErrors.endValue = '';
        }
        break;
      }

      case 'start-limit': {
        if (value === '' || value <= 0) {
          this.formValidation.shapeControls.startLimit = true;
          this.formValidation.formErrors.startLimit = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Embankment Start Slope Limit');
        } else {
          this.formValidation.shapeControls.startLimit = false;
          this.formValidation.formErrors.startLimit = '';
        }
        break;
      }

      case 'end-limit': {
        if (value === '' || value <= 0) {
          this.formValidation.shapeControls.endLimit = true;
          this.formValidation.formErrors.endLimit = 'usa-input--error';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, 'Embankment End Slope Limit');
        } else {
          this.formValidation.shapeControls.endLimit = false;
          this.formValidation.formErrors.endLimit = '';
        }
        break;
      }

      default:
        break;
    }
  }

  isAllFalse(obj) {
    for (const key in obj) {
      if (typeof obj[key] === 'object') {
        if (!this.isAllFalse(obj[key])) {
          return false;
        }
      } else if (obj[key] !== false) {
        return false;
      }
    }
    return true;
  }


  private getRequestBody(): UpdateTerraceDesignControlDTO {
    return {
      alignedElementDesignParametersId: +terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId, //12716, //desingstore.alignedDesignElementId
      terraceDesignControl: {
        shapeControlDTO: {
          overFillMode: this.shapeControls.overfillMode,
          overFillAmount: this.shapeControls.overfillAmount,
          freeboardMode: this.shapeControls.freeboardMode,
          freeboard: this.shapeControls.freeboard,
          backCutEnabled: this.shapeControls.backCutEnabled,
          minSlopeWidth: this.shapeControls.minimumSlopeWidth,
          backCutSlopeGrade: this.shapeControls.backCutSlopeGrade,
          bankStartSlope: {
            slopeName: 'bankStartSlope',
            slopeMode: this.shapeControls.startMode,
            slopeValue: this.shapeControls.startValue,
            isLimitEnforced: this.shapeControls.gradeChannelStart ? 1 : 0,
            slopeLimit: this.shapeControls.startLimit,
          },
          bankEndSlope: {
            slopeName: 'bankEndSlope',
            slopeMode: this.shapeControls.endMode,
            slopeValue: this.shapeControls.endValue,
            isLimitEnforced: this.shapeControls.gradeChannelEnd ? 1 : 0,
            slopeLimit: this.shapeControls.endLimit,
          },
        },
        orientationDTO: {
          uphillDirection: this.orientationRadioControl === 0 ? 'LEFT' : 'RIGHT',
          flagLineLocation: this.orientationFormData.flagLineLocation,
          flagLineLocationOption: JSON.stringify(this.flagLineLocationOptions),
          offsetFromAlignment: this.orientationFormData.offsetFromAlignment,
        },
        terraceTypeId: terraceDesignStore.terraceType,
        terraceTemplateId: this.defaultTemplateRadioControl,
        terraceChannelTemplateJson: JSON.stringify({
          templateName: this.reachTemplateForm.templateName,
          cutSlope: this.reachTemplateForm.slopeLimits[0].slopeValue,
          cutSlopeMode: SlopeModeChannelEnum[this.reachTemplateForm.slopeLimits[0].slopeMode],
          frontSlope: this.reachTemplateForm.slopeLimits[1].slopeValue,
          frontSlopeMode: SlopeModeChannelEnum[this.reachTemplateForm.slopeLimits[1].slopeMode],
          backSlope: this.reachTemplateForm.slopeLimits[2].slopeValue,
          backSlopeMode: SlopeModeChannelEnum[this.reachTemplateForm.slopeLimits[2].slopeMode],
          bottomWidth: this.reachTemplateForm.bottomWidth,
          frontHeight: this.reachTemplateForm.frontHeight,
          topWidth: this.reachTemplateForm.topWidth,
          comments: null,
        }),
        isUserDefinedTemplate: this.isUserDefinedTemplate,
        slopeLimits: [
          {
            slopeName: this.reachTemplateForm.slopeLimits[0].slopeName,
            slopeMode: this.reachTemplateForm.slopeLimits[0].limitingMode,
            slopeValue: +this.reachTemplateForm.slopeLimits[0].slopeValue,
            isLimitEnforced: this.reachTemplateForm.slopeLimits[0].isLimitEnforced ? 1 : 0,
            slopeLimit: +this.reachTemplateForm.slopeLimits[0].slopeLimit,
          },
          {
            slopeName: this.reachTemplateForm.slopeLimits[1].slopeName,
            slopeMode: this.reachTemplateForm.slopeLimits[1].limitingMode,
            slopeValue: +this.reachTemplateForm.slopeLimits[1].slopeValue,
            isLimitEnforced: this.reachTemplateForm.slopeLimits[1].isLimitEnforced ? 1 : 0,
            slopeLimit: +this.reachTemplateForm.slopeLimits[1].slopeLimit,
          },
          {
            slopeName: this.reachTemplateForm.slopeLimits[2].slopeName,
            slopeMode: this.reachTemplateForm.slopeLimits[2].limitingMode,
            slopeValue: +this.reachTemplateForm.slopeLimits[2].slopeValue,
            isLimitEnforced: this.reachTemplateForm.slopeLimits[2].isLimitEnforced ? 1 : 0,
            slopeLimit: +this.reachTemplateForm.slopeLimits[2].slopeLimit,
          },
        ],
      },
    };
  }

  @action.bound async handleRadioInputChange(event) {
    const { name, value } = event.target;
    terraceDesignStore.terraceInputsChanged = true;
    const splitter: string[] = name.split(' ');
    this.defaultTemplateRadioControl = parseFloat(splitter[1]);
    const requiredId = this.terraceTemplateReachesObject.findIndex((item) => item.terraceChannelTemplateID === this.defaultTemplateRadioControl);
    this.applyTemplate(this.terraceTemplateReachesObject[requiredId]);
  }

  @action.bound applyTemplate = (template) => {
    this.defaultTemplateRadioControl = template.terraceChannelTemplateID ? template.terraceChannelTemplateID : 0;
    this.templateName = template.templateName;
    this.applyValuesToTemplate(template);
    this.resetCrossSectionControlValidation();
    this.isUserDefinedTemplate = 0;
  }

  @action.bound applyValuesToTemplate = (t2) => {
    this.reachTemplateForm.templateName = t2.templateName
    this.reachTemplateForm.bottomWidth = t2.bottomWidth
    this.reachTemplateForm.topWidth = t2.topWidth
    this.reachTemplateForm.frontHeight = t2.frontHeight
    this.reachTemplateForm.slopeLimits = [];
    this.reachTemplateForm.slopeLimits.length = 0;
    const slopeLimits = t2.slopeLimits ? t2.slopeLimits : JSON.parse(t2.slopeLimitJson);
    slopeLimits.forEach(item => this.reachTemplateForm.slopeLimits.push({
      ...item,
      slopeMode: SlopeModeToLimitEnum[item.slopeMode],
      limitingMode: item.slopeMode,
    })
    )
    // copy applied template to initial values in store
    terraceDesignStore.initialTerraceTemplate = JSON.parse(JSON.stringify(this.reachTemplateForm));
  }

  @action.bound resetToInitialTemplate = () => {
    this.reachTemplateForm = JSON.parse(JSON.stringify(this.loadedInitialTemplate))
    this.isUserDefinedTemplate = this.loadedInitialTemplate.templateName === "User Defined" ? 1 : 0;
    const i = this.terraceTemplateReachesObject.findIndex((item) => item.templateName === this.loadedInitialTemplate.templateName)
    const terraceChannelTemplateID = i && this.terraceTemplateReachesObject[i] && this.terraceTemplateReachesObject[i].terraceChannelTemplateID ? this.terraceTemplateReachesObject[i].terraceChannelTemplateID : 1;
    this.defaultTemplateRadioControl = this.loadedInitialTemplate.templateName === "User Defined" ? 0 : terraceChannelTemplateID;
    this.templateName = this.loadedInitialTemplate.templateName;
    terraceDesignStore.terraceInputsChanged = false;
  }

  @action.bound toggleSelectTemplate() {
    this.displayTemplateTable = !this.displayTemplateTable;
  }

  @action.bound async fetchAllTerraceTemplatesUsingGETfromDB() {
    this.isLoading = true;
    try {
      const response = await TerraceChannelTemplateController.getAllTerraceTemplateUsingGET();
      if (response && response.data) {
        const data = response.data;
        this.terraceTemplateReachesObject = data.map((item) => {
          const newTerraceTemplate: IDefaultTerraceTemplateReachRow = {
            cutSlope: item.cutSlope ? item.cutSlope : 0,
            cutSlopeMode: item.cutSlopeMode ? item.cutSlopeMode : 0,
            frontSlope: item.frontSlope ? item.frontSlope : 0,
            frontSlopeMode: item.frontSlopeMode ? item.frontSlopeMode : 0,
            backSlope: item.backSlope ? item.backSlope : 0,
            backSlopeMode: item.backSlopeMode ? item.backSlopeMode : 0,
            bottomWidth: item.bottomWidth ? item.bottomWidth : 0,
            frontHeight: item.frontHeight ? item.frontHeight : 0,
            topWidth: item.topWidth ? item.topWidth : 0,
            terraceChannelTemplateID: item.terraceChannelTemplateId ? item.terraceChannelTemplateId : 0,
            templateName: item.templateName ? item.templateName : "",
            comments: item.comments ? item.comments : "",
            arrayOrder: item.lastChangeDateUtc ? item.lastChangeDateUtc : 0,
            slopeLimitJson: item.slopeLimitsJson ? item.slopeLimitsJson : '',
          }
          return newTerraceTemplate;
        })

      }
    } catch (error) {
      console.error(error)
    }
    finally {
      this.isLoading = false;
    }
  }
  @action.bound async validateShapeControls(shapeControls, buttonName) {
    const { freeboardMode, overfillMode, freeboard, overfillAmount } = shapeControls;
    const isFreeboardNone = freeboardMode === 'NONE';
    const isOverfillNone = overfillMode === 'NONE';
    const isBothModesNone = isFreeboardNone && isOverfillNone;
    const hasZeroValues = freeboard === 0 && overfillAmount === 0;
    const shouldValidate = isBothModesNone || (!isBothModesNone && hasZeroValues);
    if (!shouldValidate) {
      return this.calSurfaceInletStaging(buttonName);
    }
    this.shapeControlsHasWarnings = true;
    const warningOptions = buttonName === 'save' ? [] : [
      {
        callback: () => MessageCenter.clearMessages(),
        text: "Cancel",
      },
      {
        callback: async () => {
          MessageCenter.clearMessages();
          await terraceDesignStore.handleUiStateUpdate({
            uiStateType: TerraceUiState.TerraceTemplateProceed.string
          });
          this.history.push(`/design/${designStore.projectsStore.currentProjectId}/terrace/${terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId}/design`)
        },
        text: "Ok",
      },
    ];
    this.calSurfaceInletStaging(buttonName);
    await MessageCenter.addWarningFromCode('Terrace_20', warningOptions);
  }
  @action.bound showWarningOnSave = async (buttonName) => {
    const templatesAreEqual = this.templatesAreEqual(this.reachTemplateForm, this.loadedInitialTemplate);
    if (!templatesAreEqual) {
      await MessageCenter.addNotificationFromCode('Terrace_19', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.resetToInitialTemplate();
            return;
          },
          text: "Cancel",
        },
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.calSurfaceInletStaging(buttonName)
          },
          text: "Ok",
        },
      ]);
    } else {
      await this.validateShapeControls(this.shapeControls, buttonName);
    }
  }

  @action.bound async calSurfaceInletStaging(buttonName: 'save' | 'saveProceed') {
    this.isLoading = true;
    const requestBody: UpdateTerraceDesignControlDTO = this.getRequestBody();
    requestBody.inputSaveIndicator = buttonName === 'save' ? 1 : 2;
    try {
      const response = await TerraceDesignToolsController.updateTerraceDesignControlUsingPOST(requestBody);
      if (response && response.data) {
        if (response?.data?.errorResponse) {
          MessageCenter.resetMessageCenter();
          MessageCenter.addNotification(
            response.data.errorResponse.errorMessage!,
            response.data.errorResponse.errorType!,
            response.data.errorResponse.errorCode
          );
        }
        else {
          if (TerraceUiState[terraceDesignStore.uiState!].number > 14 || terraceDesignStore.summaryInfo.channelLength > 0) {
            const syncResponse = await TerraceReachController.syncTemplateToReachesUsingPOST(+terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
            if (syncResponse && syncResponse.data) {
              const syncData = syncResponse.data;
              if (syncData.errorResponse && syncData.errorResponse.errorMessage && syncData.errorResponse.errorType && syncData.errorResponse.errorCode) {
                MessageCenter.addNotification(syncData.errorResponse.errorMessage, syncData.errorResponse.errorType, syncData.errorResponse.errorCode)
              }
            }
          }
          terraceDesignStore.terraceInputsChanged = false;
          terraceDesignStore.summaryInfo.templateName = this.templateName ? this.templateName : 'User Defined';
          // if new template selected
          const uiStateType = buttonName === 'save' ? TerraceUiState.TerraceTemplateSave.string : TerraceUiState.TerraceTemplateProceed.string;
          if (buttonName !== 'save' && !this.shapeControlsHasWarnings) {
            await terraceDesignStore.handleUiStateUpdate({ uiStateType });
            this.history.push(`/design/${designStore.projectsStore.currentProjectId}/terrace/${terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId}/design`);
          }
          else {
            await terraceDesignStore.handleUiStateUpdate({
              uiStateType: TerraceUiState.TerraceTemplateSave.string
            });
          }
          // copy applied template to store and loadedInitialTemplate
          terraceDesignStore.initialTerraceTemplate = JSON.parse(JSON.stringify(this.reachTemplateForm));
          this.loadedInitialTemplate = JSON.parse(JSON.stringify(this.reachTemplateForm));
        }
      }
    } catch (error) {
      console.error(error);
    }
    this.isLoading = false;
  }

  @observable loadedInitialTemplate = JSON.parse(JSON.stringify(reachTemplateFormDefault));
  @action.bound init = async () => {
    await this.fetchAllTerraceTemplatesUsingGETfromDB();
    // On initial load of Template, get possible existing values
    await this.getSavedTemplateData();
    // copy applied template to store and loadedInitialTemplate
    terraceDesignStore.initialTerraceTemplate = JSON.parse(JSON.stringify(this.reachTemplateForm));
    this.loadedInitialTemplate = JSON.parse(JSON.stringify(this.reachTemplateForm));
  }

  @action.bound templatesAreEqual = (t1, t2) => {
    const notEqual = t1.bottomWidth !== t2.bottomWidth ||
      t1.frontHeight !== t2.frontHeight ||
      t1.topWidth !== t2.topWidth ||
      t1.slopeLimits[0].isLimitEnforced !== t2.slopeLimits[0].isLimitEnforced ||
      t1.slopeLimits[0].limitingMode !== t2.slopeLimits[0].limitingMode ||
      parseFloat(t1.slopeLimits[0].slopeLimit) !== parseFloat(t2.slopeLimits[0].slopeLimit) ||
      t1.slopeLimits[0].slopeMode !== t2.slopeLimits[0].slopeMode ||
      t1.slopeLimits[0].slopeName !== t2.slopeLimits[0].slopeName ||
      t1.slopeLimits[0].slopeValue !== t2.slopeLimits[0].slopeValue ||
      t1.slopeLimits[1].isLimitEnforced !== t2.slopeLimits[1].isLimitEnforced ||
      t1.slopeLimits[1].limitingMode !== t2.slopeLimits[1].limitingMode ||
      parseFloat(t1.slopeLimits[1].slopeLimit) !== parseFloat(t2.slopeLimits[1].slopeLimit) ||
      t1.slopeLimits[1].slopeMode !== t2.slopeLimits[1].slopeMode ||
      t1.slopeLimits[1].slopeName !== t2.slopeLimits[1].slopeName ||
      t1.slopeLimits[1].slopeValue !== t2.slopeLimits[1].slopeValue ||
      t1.slopeLimits[2].isLimitEnforced !== t2.slopeLimits[2].isLimitEnforced ||
      t1.slopeLimits[2].limitingMode !== t2.slopeLimits[2].limitingMode ||
      parseFloat(t1.slopeLimits[2].slopeLimit) !== parseFloat(t2.slopeLimits[2].slopeLimit) ||
      t1.slopeLimits[2].slopeMode !== t2.slopeLimits[2].slopeMode ||
      t1.slopeLimits[2].slopeName !== t2.slopeLimits[2].slopeName ||
      t1.slopeLimits[2].slopeValue !== t2.slopeLimits[2].slopeValue
    return !notEqual;
  }

  @computed get terraceTemplateChanged(): boolean {
    const templatesAreEqual = this.templatesAreEqual(this.reachTemplateForm, terraceDesignStore.initialTerraceTemplate);
    if (templatesAreEqual) {
      return false;
    } else {
      return true;
    }
  }

  @action.bound async getSavedTemplateData() {
    if (terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId) {
      this.isLoading = true
      // Any other checks before calling API - No result data from Inputs in store?
      try {
        // Call API
        const response = await TerraceDesignToolsController.getTerraceDesignControlByAlignedElementIdUsingGET(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        // Handle Response with valid data
        if (response && response.data) {
          if (response.data.errorResponse && response.data.errorResponse.errorCode !== undefined) {
            if (terraceDesignStore.summaryInfo.templateName !== '') {
              MessageCenter.addNotification(response.data.errorResponse.errorMessage!, response.data.errorResponse.errorType!, response.data.errorResponse.errorCode);
            }
          }
          else if (response.data.terraceDesignControlDTO) {
            await this.assignTempateValuesFromApi(response.data.terraceDesignControlDTO)
          }
        }
        else {
          // No valid response or data object
          console.error('Good http with no data in response.data objects')
        }
      }
      catch (error) {
        // It seems this is the case for no previously saved template. This case will have more information
        // to avoid the API call with UI State Management stories.
        console.error(error);
      }
      finally {
        this.isLoading = false;
      }
    }
  }

  @action.bound async getTerraceChannelTemplateReqValues() {
    if (terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId) {
      this.isLoading = true
      try {
        const response = await TerraceChannelTemplateController.getDefaultTerraceDesignControlChannelTemplateUsingGET(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        // Handle Response with valid data
        if (response && response.data) {
          return response.data;
        }
      }
      catch (error) {
        console.error(error)
      }
      finally {
        this.isLoading = false;
      }
    }

  }

  @action.bound async assignTempateValuesFromApi(apiResponse: TerraceDesignControlDTO) {
    const { orientationDTO, shapeControlDTO, slopeLimits, terraceChannelTemplateJson, terraceTemplateId } = apiResponse;
    let parsedTemplate;
    if (terraceChannelTemplateJson === '') {
      parsedTemplate = await this.getTerraceChannelTemplateReqValues()
    } else {
      parsedTemplate = JSON.parse(terraceChannelTemplateJson!);
    }
    // Orientation
    this.orientationRadioControl = orientationDTO?.uphillDirection?.toLowerCase() === 'left' ? 0 : 1;
    this.orientationFormData.flagLineLocation = orientationDTO?.flagLineLocation ? orientationDTO?.flagLineLocation : TemplateDefaults.orientationFormData.flagLineLocation;
    this.orientationFormData.offsetFromAlignment = this.checkAllowedZero(orientationDTO?.offsetFromAlignment) ? orientationDTO?.offsetFromAlignment! : TemplateDefaults.orientationFormData.offsetFromAlignment;

    if (terraceTemplateId && terraceTemplateId !== 0) {
      // if terrace has one of default templates
      this.defaultTemplateRadioControl = terraceTemplateId;
      const requiredId = this.terraceTemplateReachesObject.findIndex((item) => item.terraceChannelTemplateID === this.defaultTemplateRadioControl);
      this.applyTemplate(this.terraceTemplateReachesObject[requiredId]);
    } else if (terraceChannelTemplateJson) {
      // if terrace has user defined template
      this.defaultTemplateRadioControl = 0;
      this.displayTemplateTable = false;
      this.isUserDefinedTemplate = 1;
      // Cross Section Controls
      this.assignCrossSectionControls(slopeLimits, parsedTemplate);
    } else {
      // if new terrace, assign first default template
      this.applyTemplate(this.terraceTemplateReachesObject[0]);
      this.isUserDefinedTemplate = 0;
    }

    // Shape Controls
    this.assignShapeControls(shapeControlDTO);
  }

  assignCrossSectionControls(slopeLimits: TerraceSlopeAndLimits[] | undefined, parsedTemplate) {
    this.templateName = parsedTemplate.templateName ? parsedTemplate.templateName : 'User Defined'
    this.reachTemplateForm.templateName = parsedTemplate.templateName ? parsedTemplate.templateName : 'User Defined'
    this.reachTemplateForm.bottomWidth = this.checkAllowedZero(parsedTemplate.bottomWidth) ? parsedTemplate.bottomWidth : reachTemplateFormDefault.bottomWidth;
    this.reachTemplateForm.frontHeight = this.checkAllowedZero(parsedTemplate.frontHeight) ? parsedTemplate.frontHeight : reachTemplateFormDefault.frontHeight;
    this.reachTemplateForm.topWidth = this.checkAllowedZero(parsedTemplate.topWidth) ? parsedTemplate.topWidth : reachTemplateFormDefault.topWidth;
    const cutSlope = slopeLimits?.find((obj) => obj.slopeName === 'cutSlope')
    const frontSlope = slopeLimits?.find((obj) => obj.slopeName === 'frontSlope')
    const backSlope = slopeLimits?.find((obj) => obj.slopeName === 'backSlope')
    if (this.checkSlopeLimitValues(cutSlope)) {
      this.reachTemplateForm.slopeLimits[0] = {
        slopeName: cutSlope!.slopeName!,
        slopeMode: SlopeModeToLimitEnum[cutSlope!.slopeMode!],
        slopeValue: cutSlope!.slopeValue!,
        isLimitEnforced: cutSlope!.isLimitEnforced!,
        limitingMode: cutSlope!.slopeMode!,
        slopeLimit: cutSlope!.slopeLimit!
      };
    } else {
      this.reachTemplateForm.slopeLimits[0] = reachTemplateFormDefault.slopeLimits[0];
    }
    if (this.checkSlopeLimitValues(frontSlope)) {
      this.reachTemplateForm.slopeLimits[1] = {
        slopeName: frontSlope!.slopeName!,
        slopeMode: SlopeModeToLimitEnum[frontSlope!.slopeMode!],
        slopeValue: frontSlope!.slopeValue!,
        isLimitEnforced: frontSlope!.isLimitEnforced!,
        limitingMode: frontSlope!.slopeMode!,
        slopeLimit: frontSlope!.slopeLimit!
      };
    } else {
      this.reachTemplateForm.slopeLimits[1] = reachTemplateFormDefault.slopeLimits[1];
    }
    if (this.checkSlopeLimitValues(backSlope)) {
      this.reachTemplateForm.slopeLimits[2] = {
        slopeName: backSlope!.slopeName!,
        slopeMode: SlopeModeToLimitEnum[backSlope!.slopeMode!],
        slopeValue: backSlope!.slopeValue!,
        isLimitEnforced: backSlope!.isLimitEnforced!,
        limitingMode: backSlope!.slopeMode!,
        slopeLimit: backSlope!.slopeLimit!
      };
    } else {
      this.reachTemplateForm.slopeLimits[2] = reachTemplateFormDefault.slopeLimits[2];
    }
    // copy initial values to store
    terraceDesignStore.initialTerraceTemplate = JSON.parse(JSON.stringify(this.reachTemplateForm));
  }
  assignShapeControls(shapeControlDTO: ShapeControlDTO | undefined) {
    this.shapeControls.overfillMode = shapeControlDTO?.overFillMode ? shapeControlDTO.overFillMode : TemplateDefaults.shapeControls.overfillMode;
    this.shapeControls.overfillAmount = this.checkAllowedZero(shapeControlDTO?.overFillAmount) ? shapeControlDTO!.overFillAmount! : TemplateDefaults.shapeControls.overfillAmount;
    this.shapeControls.overfillAmountDisabled = this.shapeControls.overfillMode === "NONE" ? true : false;
    this.shapeControls.freeboardMode = shapeControlDTO?.freeboardMode ? shapeControlDTO.freeboardMode : TemplateDefaults.shapeControls.freeboardMode;
    this.shapeControls.freeboard = this.checkAllowedZero(shapeControlDTO?.freeboard) ? shapeControlDTO!.freeboard! : TemplateDefaults.shapeControls.freeboard;
    this.shapeControls.freeboardDisabled = this.shapeControls.freeboardMode === "NONE" ? true : false;
    this.shapeControls.startMode = shapeControlDTO?.bankStartSlope?.slopeMode ? shapeControlDTO.bankStartSlope?.slopeMode : TemplateDefaults.shapeControls.startMode;
    this.shapeControls.startValue = shapeControlDTO?.bankStartSlope?.slopeValue ? shapeControlDTO.bankStartSlope?.slopeValue : TemplateDefaults.shapeControls.startValue;
    this.shapeControls.startLimit = shapeControlDTO?.bankStartSlope?.slopeLimit ? shapeControlDTO.bankStartSlope?.slopeLimit : TemplateDefaults.shapeControls.startLimit;
    this.shapeControls.endMode = shapeControlDTO?.bankEndSlope?.slopeMode ? shapeControlDTO.bankEndSlope?.slopeMode : TemplateDefaults.shapeControls.endMode;
    this.shapeControls.endValue = shapeControlDTO?.bankEndSlope?.slopeValue ? shapeControlDTO.bankEndSlope?.slopeValue : TemplateDefaults.shapeControls.endValue;
    this.shapeControls.endLimit = shapeControlDTO?.bankEndSlope?.slopeLimit ? shapeControlDTO.bankEndSlope?.slopeLimit : TemplateDefaults.shapeControls.endLimit;
    this.shapeControls.gradeChannelStart = !!shapeControlDTO?.bankStartSlope?.isLimitEnforced;
    this.shapeControls.gradeChannelEnd = !!shapeControlDTO?.bankEndSlope?.isLimitEnforced;
    this.shapeControls.backCutEnabled = shapeControlDTO?.backCutEnabled ? shapeControlDTO.backCutEnabled : TemplateDefaults.shapeControls.backCutEnabled;
    this.shapeControls.minimumSlopeWidth = shapeControlDTO?.minSlopeWidth ? shapeControlDTO.minSlopeWidth : TemplateDefaults.shapeControls.minimumSlopeWidth;
    this.shapeControls.backCutSlopeGrade = shapeControlDTO?.backCutSlopeGrade ? shapeControlDTO.backCutSlopeGrade : TemplateDefaults.shapeControls.backCutSlopeGrade;
  }
  checkSlopeLimitValues(slopeObj: TerraceSlopeAndLimits | undefined): boolean {
    // True if valid, false if undefined or property is invalid.
    // blanket chack to just use a default slope set if any value isn't present.
    if (slopeObj === undefined) {
      return false;
    }
    const values = Object.values(slopeObj);
    return !(values.includes('') || values.includes(undefined) || values.includes(null));
  }
  checkAllowedZero(value): boolean {
    if (value === undefined || value === null) {
      return false;
    }
    return true;
  }
}



export const terrraceVertexParamsOnDesign = async () => {
  try {
    const response = await TerraceDesignToolsController.getTerraceVertexParamsDtoByAlignedElementIdUsingGET(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)
    if (response && response.data) {
      terraceDesignStore.terraceDesignObject.terraceDesignStations = response.data.map(item => ({
        ...item,
        templateParams: {
          ...item.templateParams,
          // Add missing properties not in response
          backSlope: item.templateParams?.backSlope
        },
        drainStationDto: {
          ...item.drainStationDto,
          location: {
            ...item.drainStationDto?.location
          }
        },
        blockStationDto: {
          ...item.blockStationDto,
          isSplitSubbasin: item.blockStationDto?.splitSubbasin,
          location: {
            ...item.blockStationDto?.location,
            wkid: item.blockStationDto?.location?.cutOrFill
          }
        },
        errorResponse: {
          ...item.errorResponse
        },
        // Ensure nullable fields are properly handled
        liningMaterial: item.liningMaterial || null,
        templateName: item.templateName || null
      })) as ITerraceVertexParamsDTO[];

    }
  }
  catch (error) {
    console.error(error)
  }
}
